import { store } from "api/store";

import { generateHMAC } from "./utils";

const INTERCOM_APP_ID = "dseazjfq";
const INTERCOM_API_BASE = "https://api-iam.intercom.io";
const INTERCOM_COMPANY_NAME = "Socials by PostPaddy";
const INTERCOM_COMPANY_ID = "postpaddy";
const INTERCOM_BOOT_KEY = "__postpaddy_intercom_boot_key__";

const getIntercom = () => window.Intercom;

const getCurrentUser = (user) => user ?? store.getState().user;

const getDefaultCompany = () => ({
  id: INTERCOM_COMPANY_ID,
  name: INTERCOM_COMPANY_NAME,
});

const getBootPayload = (user, identity) => {
  if (identity) {
    return {
      bootKey: JSON.stringify(identity),
      payload: {
        app_id: INTERCOM_APP_ID,
        companies: [getDefaultCompany()],
        ...identity,
      },
    };
  }

  if (!user?.email) {
    return null;
  }

  const hmacSecret = import.meta.env.VITE_INTERCOM_KEY;

  return {
    bootKey: `${user.email}:${user.full_name ?? ""}`,
      payload: {
        app_id: INTERCOM_APP_ID,
        name: user.full_name,
        email: user.email,
        user_hash: hmacSecret ? generateHMAC(user.email, hmacSecret) : undefined,
        companies: [getDefaultCompany()],
      },
  };
};

export const configureIntercomLauncher = (
  customLauncherSelector,
  hideDefaultLauncher = false
) => {
  const settings = {
    api_base: INTERCOM_API_BASE,
    app_id: INTERCOM_APP_ID,
    hide_default_launcher: hideDefaultLauncher,
  };

  if (customLauncherSelector) {
    settings.custom_launcher_selector = customLauncherSelector;
  }

  window.intercomSettings = settings;
};

export const ensureIntercomBooted = (user, options = {}) => {
  const intercom = getIntercom();
  if (!intercom) {
    return false;
  }

  const { customLauncherSelector, hideDefaultLauncher = false, identity } =
    options;
  const bootPayload = getBootPayload(getCurrentUser(user), identity);

  configureIntercomLauncher(customLauncherSelector, hideDefaultLauncher);

  if (!bootPayload) {
    if (window[INTERCOM_BOOT_KEY] !== "anonymous") {
      if (window[INTERCOM_BOOT_KEY]) {
        intercom("shutdown");
      }

      intercom("boot", {
        app_id: INTERCOM_APP_ID,
      });
      window[INTERCOM_BOOT_KEY] = "anonymous";
    }
    return true;
  }

  if (window[INTERCOM_BOOT_KEY] === bootPayload.bootKey) {
    return true;
  }

  if (window[INTERCOM_BOOT_KEY]) {
    intercom("shutdown");
  }

  intercom("boot", bootPayload.payload);
  window[INTERCOM_BOOT_KEY] = bootPayload.bootKey;

  return true;
};

export const updateIntercom = (user, options = {}, extra = {}) => {
  const intercom = getIntercom();
  if (!intercom) {
    return false;
  }

  const { customLauncherSelector, hideDefaultLauncher = false, identity } =
    options;
  const bootPayload = getBootPayload(getCurrentUser(user), identity);

  configureIntercomLauncher(customLauncherSelector, hideDefaultLauncher);

  intercom("update", {
    ...(bootPayload?.payload ?? {}),
    ...extra,
    last_request_at: Math.floor(Date.now() / 1000),
  });

  return true;
};

export const openSupportChat = (user, options = {}) => {
  const intercom = getIntercom();
  if (!intercom) {
    return false;
  }

  const isBooted = ensureIntercomBooted(user, options);
  if (!isBooted) {
    return false;
  }

  intercom("showMessages");
  return true;
};

export const shutdownIntercom = () => {
  const intercom = getIntercom();
  if (!intercom) {
    return;
  }

  intercom("shutdown");
  window[INTERCOM_BOOT_KEY] = undefined;
};

export const resetIntercomSession = () => {
  shutdownIntercom();
};
